import { WIX_INNER_ELEMENT_TAG } from '../../paranative/constants'

const MASTER_PAGE_ID = 'masterPage'
const ROOT_SELECTOR = ':root'

interface ComponentSelector {
    type: 'component'
    componentId: string
}
interface InnerElementSelector {
    type: 'innerElement'
    componentId: string
    elementPath: readonly string[]
}
interface GlobalStyleSelector {
    type: 'globalStyle'
    styleId: string
}

export type CssSelector = ComponentSelector | InnerElementSelector | GlobalStyleSelector
const COMPONENT_SELECTOR_PATTERN = /^#([^\s]+)$/
const GLOBAL_STYLE_SELECTOR_PATTERN = /^\.([^\s]+)$/

type CssSelectorStrategyMap = Record<
    CssSelector['type'],
    {
        stringify(selector: CssSelector): string
        parse(selector: string): CssSelector | undefined
    }
>

const cssSelectorStrategy: CssSelectorStrategyMap = {
    component: {
        stringify: (selector: ComponentSelector) =>
            selector.componentId === MASTER_PAGE_ID ? ROOT_SELECTOR : `#${selector.componentId}`,
        parse: (selector: string) => {
            if (selector === ROOT_SELECTOR) {
                return { type: 'component', componentId: MASTER_PAGE_ID }
            }
            const componentId = selector.match(COMPONENT_SELECTOR_PATTERN)?.[1]
            if (!componentId) {
                return undefined
            }
            return { type: 'component', componentId }
        },
    },
    innerElement: {
        stringify: (selector: InnerElementSelector) => {
            const chainedSelectors = selector.elementPath
                .map(name => `${WIX_INNER_ELEMENT_TAG}[data-name="${name}"]`)
                .join(' > ')
            return `#${selector.componentId} > ${chainedSelectors}`
        },
        parse: (selector: string) => {
            const [componentPart, ...innerElementParts] = selector.split(' > ')
            const componentId = componentPart.match(/^#(.+)/)?.[1]
            if (!componentId) {
                return undefined
            }
            const elementPath = innerElementParts.map(segment => segment.match(/data-name="([^"]+)"/)?.[1])
            if (elementPath.some(name => !name)) {
                return undefined
            }
            return { type: 'innerElement', componentId, elementPath: elementPath as string[] }
        },
    },
    globalStyle: {
        stringify: (selector: GlobalStyleSelector) => `.${selector.styleId}`,
        parse: (selector: string) => {
            const styleId = selector.match(GLOBAL_STYLE_SELECTOR_PATTERN)?.[1]
            if (!styleId) {
                return undefined
            }
            return { type: 'globalStyle', styleId }
        },
    },
}

export const buildSelector = (selector: CssSelector): string => {
    return cssSelectorStrategy[selector.type].stringify(selector)
}

export const parseSelector = (selector: string): CssSelector => {
    const parsedSelector =
        cssSelectorStrategy.component.parse(selector) ??
        cssSelectorStrategy.innerElement.parse(selector) ??
        cssSelectorStrategy.globalStyle.parse(selector)

    if (!parsedSelector) {
        throw new Error(`Invalid selector: ${selector}`)
    }

    return parsedSelector
}
